// src/App.tsx
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import PageErrorBoundary from './components/PageErrorBoundary';
import { TabProvider } from './TabContext';
import { AuthProvider } from './contexts/AuthContext';
import { UiPreferencesProvider } from './contexts/UiPreferencesContext';
import ProtectedRoute from './components/ProtectedRoute';
import { Analytics } from '@vercel/analytics/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';

// Import components
import { Masthead } from './components/design';
import WalletInput from './components/WalletInput';
import CommandPalette from './components/CommandPalette';
import ToastContainer from './components/ToastContainer';
import { useCommandPalette } from './hooks/useCommandPalette';
import { useToast } from './hooks/useToast';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const PerpsData = lazy(() => import('./pages/PerpsData'));
const OpenOrders = lazy(() => import('./pages/OpenOrders'));
const TradeHistory = lazy(() => import('./pages/TradeHistory'));
const Market = lazy(() => import('./pages/Market'));
const CoinDetails = lazy(() => import('./pages/CoinDetails'));
const MarketTrends = lazy(() => import('./pages/MarketTrends'));
const WhaleFlow = lazy(() => import('./pages/WhaleFlow'));
const Help = lazy(() => import('./pages/Help'));
const HelpPages = lazy(() => import('./pages/HelpPages'));
const HelpCharts = lazy(() => import('./pages/HelpCharts'));
const HelpIndicators = lazy(() => import('./pages/HelpIndicators'));
const HelpAnalysis = lazy(() => import('./pages/HelpAnalysis'));
const ChartImagePage = lazy(() => import('./pages/ChartImagePage'));
const Settings = lazy(() => import('./pages/Settings'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const TATAFPage = lazy(() => import('./pages/TATAF'));
const ThemeDetail = lazy(() => import('./pages/ThemeDetail'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const WaitlistPage = lazy(() => import('./components/WaitlistPage'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));

function useQuery(): URLSearchParams {
  return new URLSearchParams(useLocation().search);
}

const AppContent = () => {
  const { isOpen: paletteOpen, setIsOpen: setPaletteOpen } = useCommandPalette();
  const { toasts, addToast, removeToast } = useToast();
  const [wallet, setWallet] = useState<string>('');
  const query = useQuery();
  const urlWallet = query.get('wallet');
  const location = useLocation();
  const vaultMode = (location.state as { vaultMode?: boolean } | null)?.vaultMode;

  // Check if current route should show header
  const isPublicRoute = ['/', '/auth', '/waitlist', '/reset-password'].includes(location.pathname);

  useEffect(() => {
    if (urlWallet && !wallet) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setWallet(urlWallet);
    }
  }, [urlWallet, wallet]);

  return (
    <div className="min-h-screen bg-dark-charcoal">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      {/* Only show header on protected routes */}
      {!isPublicRoute && <Masthead />}
      <main id="main-content" tabIndex={-1}>
        <Suspense
          fallback={
            <div className="app-page">
              <div
                className="panel"
                style={{
                  margin: '32px auto',
                  maxWidth: 480,
                  padding: '24px 28px',
                  textAlign: 'center',
                  color: 'var(--text-2)',
                  fontFamily: 'var(--mono)',
                  fontSize: 12,
                  letterSpacing: '0.06em',
                }}
              >
                ◐ LOADING PAGE…
              </div>
            </div>
          }
        >
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/waitlist" element={<WaitlistPage />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    {vaultMode ? (
                      <Dashboard />
                    ) : wallet ? (
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      React.createElement(Dashboard as any, { wallet })
                    ) : (
                      <WalletInput onSubmit={setWallet} />
                    )}
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/perps-data"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    <PerpsData />
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/open-orders"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    <OpenOrders wallet={wallet || urlWallet} />
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/trade-history"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    <TradeHistory wallet={wallet || urlWallet} />
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/live-market"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    <Market />
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/market-trends"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    <MarketTrends />
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/whale-flow"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    <WhaleFlow />
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/coin/:coin"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    <CoinDetails />
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/help"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    <Help />
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/help/pages"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    <HelpPages />
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/help/charts"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    <HelpCharts />
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/help/indicators"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    <HelpIndicators />
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/help/analysis"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    <HelpAnalysis />
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/chart-image"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    <ChartImagePage />
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />

            {/* Redirect any unknown routes to landing page */}
            <Route
              path="/tataf"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    <TATAFPage />
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tataf/themes/:id"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    <ThemeDetail />
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portfolio"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    <Portfolio />
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <PageErrorBoundary>
                    <Settings />
                  </PageErrorBoundary>
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <CommandPalette isOpen={paletteOpen} onClose={() => setPaletteOpen(false)} />
        <ToastContainer toasts={toasts} onDismiss={removeToast} />
      </main>
    </div>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <UiPreferencesProvider>
            <TabProvider>
              <AppContent />
              <Analytics />
            </TabProvider>
          </UiPreferencesProvider>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
